import { useLanguage } from '../../../contexts/LanguageContext';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import styles from './SoftGrid.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt, faSearch, faTimes, faTrash, faDownload, faColumns, faChevronUp, faChevronDown, faCheck } from '@fortawesome/free-solid-svg-icons';
import { downloadAsCSV } from '../../../Helpers/excelHelpers';
import { exportToPdf } from '../../../Helpers/pdfHelper';
import Pagination from '../Pagination';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Dropdown, DropdownItem, DropdownCheckboxItem, DropdownDivider } from '../Dropdown/Dropdown';
import ApiServices from '../../Services/ApiServices';
import { toastHelper } from '../../../Helpers/toastHelper';
import DeleteAlert from '../DeleteAlert';

const SummaryPill = ({ label, value, tone = 'default' }) => (
  <div className={`${styles.summaryPill} ${styles[`summaryPill${tone}`] || ''}`}>
    <span className={styles.summaryPillLabel}>{label}</span>
    <span className={styles.summaryPillValue}>{value}</span>
  </div>
);

const SearchInput = ({ value, onChange, onClear, onSearch, placeholder, inputRef }) => {
  const { t } = useLanguage();
  const [isFocused, setIsFocused] = useState(false);

  const handleKeyDown = (event) => {
    if (event.key === 'Escape' && value) {
      onClear();
      inputRef.current?.blur();
    } else if (event.key === 'Enter') {
      onSearch();
    }
  };

  return (
    <div className={`${styles.searchWrapper} ${isFocused ? styles.searchWrapperFocused : ''}`}>
      <div className={styles.searchIconContainer}>
        <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
      </div>
      <div className={styles.searchInputContainer}>
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={value}
          required={true}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          className={styles.searchInput}
          aria-label="Search table"
          role="searchbox"
        />
        <button
          onClick={() => {
            onClear();
          }}
          className={styles.clearButton}
          aria-label="Clear search"
          title={t('clearSearch')}
          type="button"
          style={{ opacity: value ? 1 : 0, pointerEvents: value ? 'auto' : 'none' }}
        >
          <FontAwesomeIcon icon={faTimes} className={styles.clearIcon} />
        </button>
      </div>
      <button
        onClick={onSearch}
        className={styles.searchButton}
        aria-label="Search"
        title={t('search')}
        type="button"
      >{t('search')}</button>
    </div>
  );
};

const EmptyState = ({ message, isSearch, onAction }) => {
  const { t } = useLanguage();
  return (
    <div className={styles.emptyStateContainer}>
      <div className={styles.emptyStateIcon}>
        {isSearch ? (
          <FontAwesomeIcon icon={faSearch} className={styles.emptyStateSvg} />
        ) : (
          <FontAwesomeIcon icon={faFileAlt} className={styles.emptyStateSvg} />
        )}
      </div>
      <h3 className={styles.emptyStateTitle}>
        {isSearch ? t('noResultsSpace') : t('noDataAvailable')}
      </h3>
      <p className={styles.emptyStateMessage}>
        {isSearch
          ? t('tryModifyingSearch')
          : message || t('noResultsAtThisTime')}
      </p>
    </div>
  );
};

const SoftGrid = ({


  columns,
  data,
  onSort,
  onSearch,
  isLoading = false,
  emptyStateMessage,
  searchPlaceholder,
  actions = [],
  setStatusWordColor = '',
  onDeleteSelected,
  multiSelect = false,
  multiSelectAPI = '',
  multiSelectIdKey = 'id',
  onMultiSelectDeleteSuccess,
  exportFilename = 'export',
  isPaginated = false,
  pageNumber = 1,
  pageSize = 10,
  totalPages = 1,
  totalRecords = 0,
  onPageChange,
  onPageSizeChange,
  useAISearch = false,
  AISearchEndpoint = '',
  columnResizable = false,
  useExportButtons = true,
  isColumnDraggable = false,
  isColumnSelectable = false,
  useGroupBy = false,
}) => {
  const { t, language } = useLanguage();
  const direction = language === 'en' ? 'ltr' : 'rtl';
  const [sortConfig, setSortConfig] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [groupByColumn, setGroupByColumn] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showDeleteButton, setShowDeleteButton] = useState(false);
  const [multiDeleteLoading, setMultiDeleteLoading] = useState(false);
  const [showMultiDeleteAlert, setShowMultiDeleteAlert] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [columnWidths, setColumnWidths] = useState({});
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeColumnKey, setResizeColumnKey] = useState(null);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);
  const [columnOrder, setColumnOrder] = useState([]);
  const [draggedColumnKey, setDraggedColumnKey] = useState(null);
  const [dragOverColumnKey, setDragOverColumnKey] = useState(null);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(new Set());
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [activeRowKey, setActiveRowKey] = useState(null);
  const [hasScrollTop, setHasScrollTop] = useState(false);
  const [showScrollShadowLeft, setShowScrollShadowLeft] = useState(false);
  const [showScrollShadowRight, setShowScrollShadowRight] = useState(false);
  const [contextMenu, setContextMenu] = useState({
    isOpen: false,
    x: 0,
    y: 0,
    row: null,
    rowActions: [],
  });
  const columnSelectorRef = useRef(null);
  const tableRef = useRef(null);
  const tableWrapperRef = useRef(null);
  const searchInputRef = useRef(null);
  const contextMenuRef = useRef(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');

    const handleMediaChange = (event) => {
      setIsMobileView(event.matches);
    };

    setIsMobileView(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleMediaChange);
      return () => mediaQuery.removeEventListener('change', handleMediaChange);
    }

    mediaQuery.addListener(handleMediaChange);
    return () => mediaQuery.removeListener(handleMediaChange);
  }, []);

  useEffect(() => {
    if (columnResizable) {
      const initialWidths = {};
      // Initialize selection column width
      if (multiSelect) {
        initialWidths['selection'] = 50;
      }
      // Initialize data columns
      columns.forEach((column) => {
        initialWidths[column.key] = column.width || 150;
      });
      setColumnWidths(initialWidths);
    }
  }, [columns, columnResizable, multiSelect]);

  useEffect(() => {
    if (isColumnDraggable) {
      const allColumnKeys = [];
      if (multiSelect) {
        allColumnKeys.push('selection');
      }
      columns.forEach((col) => allColumnKeys.push(col.key));
      if (columnOrder.length === 0 || columnOrder.length !== allColumnKeys.length) {
        setColumnOrder(allColumnKeys);
      }
    }
  }, [isColumnDraggable, columns, multiSelect, columnOrder.length]);

  useEffect(() => {
    if (isColumnSelectable) {
      setVisibleColumnKeys(new Set(columns.map(col => col.key)));
    }
  }, [isColumnSelectable, columns]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnSelectorRef.current && !columnSelectorRef.current.contains(event.target)) {
        setShowColumnSelector(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleGlobalSearchShortcut = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleGlobalSearchShortcut);
    return () => window.removeEventListener('keydown', handleGlobalSearchShortcut);
  }, []);

  useEffect(() => {
    const closeContextMenu = () => {
      setContextMenu((prev) => (prev.isOpen ? { ...prev, isOpen: false } : prev));
    };

    const handleClickOutside = (event) => {
      if (!contextMenuRef.current) return;
      if (!contextMenuRef.current.contains(event.target)) {
        closeContextMenu();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeContextMenu();
      }
    };

    if (contextMenu.isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', closeContextMenu, true);
      window.addEventListener('resize', closeContextMenu);
      window.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', closeContextMenu, true);
      window.removeEventListener('resize', closeContextMenu);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [contextMenu.isOpen]);

  useEffect(() => {
    const recalculateScrollShadows = () => {
      const wrapper = tableWrapperRef.current;
      if (!wrapper || isMobileView) {
        setHasScrollTop(false);
        setShowScrollShadowLeft(false);
        setShowScrollShadowRight(false);
        return;
      }

      const maxHorizontal = wrapper.scrollWidth - wrapper.clientWidth;
      const left = wrapper.scrollLeft;
      setHasScrollTop(wrapper.scrollTop > 0);
      setShowScrollShadowRight(left > 1);
      setShowScrollShadowLeft(maxHorizontal - left > 1);
    };

    recalculateScrollShadows();

    const wrapper = tableWrapperRef.current;
    wrapper?.addEventListener('scroll', recalculateScrollShadows);
    window.addEventListener('resize', recalculateScrollShadows);

    return () => {
      wrapper?.removeEventListener('scroll', recalculateScrollShadows);
      window.removeEventListener('resize', recalculateScrollShadows);
    };
  }, [isMobileView, data, columns.length, actions.length, pageNumber, pageSize]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || !resizeColumnKey || !columnResizable || !tableRef.current) return;
      const deltaX = e.clientX - resizeStartX;

      // Find the column being resized
      let col = columns.find(col => col.key === resizeColumnKey);
      if (!col) {
        if (resizeColumnKey === 'selection') {
          col = { key: 'selection', minWidth: 50 };
        }
      }

      const tableWidth = tableRef.current.offsetWidth;

      // Build resizable columns list (exclude selection column as it's fixed)
      const resizableColumnKeys = [];
      if (multiSelect) {
        // selection is fixed, skip it
      }
      columns.forEach(c => resizableColumnKeys.push(c.key));
      const resizableColumns = resizableColumnKeys.filter(key => key !== 'selection');

      // Calculate fixed columns total width (only selection column is truly fixed)
      const fixedTotal = multiSelect ? 50 : 0;

      // Available width for resizable columns
      const availableWidth = tableWidth - fixedTotal;

      // Calculate current total width of resizable columns
      const currentResizableTotal = resizableColumns.reduce((sum, key) => {
        const w = columnWidths[key];
        if (!w) {
          // Get default width from column definition
          const colDef = columns.find(c => c.key === key);
          return sum + (colDef?.width || 150);
        }
        return sum + (typeof w === 'number' ? w : parseFloat(w) || 0);
      }, 0);

      // Calculate the new width with constraints
      const requestedWidth = resizeStartWidth + deltaX;
      const minWidth = col?.minWidth || 50;
      const maxWidth = availableWidth - (currentResizableTotal - resizeStartWidth) - (resizableColumns.length - 1) * minWidth;
      const newWidth = Math.max(minWidth, Math.min(requestedWidth, maxWidth));

      const widthDelta = newWidth - resizeStartWidth;

      // Adjust other resizable columns proportionally
      setColumnWidths(prev => {
        const updated = { ...prev, [resizeColumnKey]: newWidth };

        const otherResizableColumns = resizableColumns.filter(key => key !== resizeColumnKey);
        if (otherResizableColumns.length > 0) {
          // Calculate new total for other columns
          const newResizableTotal = currentResizableTotal + widthDelta;
          const otherColumnsNewTotal = newResizableTotal - newWidth;

          // Distribute remaining width proportionally
          const otherColumnsCurrentTotal = otherResizableColumns.reduce((sum, key) => {
            const w = prev[key];
            if (!w) {
              const colDef = columns.find(c => c.key === key);
              return sum + (colDef?.width || 150);
            }
            return sum + (typeof w === 'number' ? w : parseFloat(w) || 0);
          }, 0);

          if (otherColumnsCurrentTotal > 0) {
            otherResizableColumns.forEach(key => {
              const currentWidth = prev[key];
              let numWidth;
              if (!currentWidth) {
                const colDef = columns.find(c => c.key === key);
                numWidth = colDef?.width || 150;
              } else {
                numWidth = typeof currentWidth === 'number' ? currentWidth : parseFloat(currentWidth) || 0;
              }
              const proportion = numWidth / otherColumnsCurrentTotal;
              const colDef = columns.find(c => c.key === key);
              const newColWidth = Math.max(colDef?.minWidth || 50, otherColumnsNewTotal * proportion);
              updated[key] = newColWidth;
            });
          }
        }

        return updated;
      });
    };
    const handleMouseUp = () => {
      setIsResizing(false);
      setResizeColumnKey(null);
    };
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeColumnKey, resizeStartX, resizeStartWidth, columns, columnResizable, columnWidths, multiSelect]);

  useEffect(() => {
    setShowDeleteButton(selectedRows.size > 0);
  }, [selectedRows]);

  const handleSort = (columnKey) => {
    const direction =
      sortConfig?.key === columnKey && sortConfig.direction === 'asc'
        ? 'desc'
        : 'asc';
    setSortConfig({ key: columnKey, direction });
    onSort?.(columnKey, direction);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchClick = () => {
    onSearch?.(searchTerm);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    onSearch?.('', undefined);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIndices = new Set(filteredAndSortedData.map((_, index) => index));
      setSelectedRows(allIndices);
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (index) => {
    const newSelectedRows = new Set(selectedRows);
    if (newSelectedRows.has(index)) {
      newSelectedRows.delete(index);
    } else {
      newSelectedRows.add(index);
    }
    setSelectedRows(newSelectedRows);
  };

  const handleDeleteSelected = () => {
    const selectedData = filteredAndSortedData.filter((_, index) => selectedRows.has(index));
    onDeleteSelected?.(selectedData);
    setSelectedRows(new Set());
  };

  const handleMultiSelectDelete = () => {
    if (!multiSelectAPI || selectedRows.size === 0) return;
    setShowMultiDeleteAlert(true);
  };

  const confirmMultiSelectDelete = async () => {
    const selectedData = filteredAndSortedData.filter((_, index) => selectedRows.has(index));
    const selectedIds = selectedData.map(row => row[multiSelectIdKey]).filter(id => id !== undefined && id !== null);
    if (selectedIds.length === 0) {
      setShowMultiDeleteAlert(false);
      return;
    }

    setMultiDeleteLoading(true);
    try {
      await ApiServices.deleteWithData(multiSelectAPI, selectedIds);
      toastHelper.success(
        t('softGrid_deleteSelectedSuccess').replace('{count}', String(selectedIds.length))
      );
      setSelectedRows(new Set());
      onMultiSelectDeleteSuccess?.();
    } catch (error) {
      toastHelper.error(error?.msg || t('softGrid_deleteSelectedError'));
    } finally {
      setMultiDeleteLoading(false);
      setShowMultiDeleteAlert(false);
    }
  };

  const handleExportExcel = () => {
    const exportColumns = columns.filter(col => col.key !== 'selection' && col.key !== 'actions');
    const headers = {};
    exportColumns.forEach(col => {
      headers[col.key] = String(col.label);
    });

    const exportData = filteredAndSortedData.map((row) => {
      const exportRow = {};
      exportColumns.forEach((col) => {
        if (typeof col.exportValue === 'function') {
          exportRow[col.key] = col.exportValue(row[col.key], row);
        } else {
          exportRow[col.key] = row[col.key];
        }
      });
      return exportRow;
    });
    downloadAsCSV(exportData, exportFilename, headers);
  };

  const handleExportPdf = () => {
    exportToPdf({
      data: filteredAndSortedData,
      columns: columns
        .filter(col => col.key !== 'selection' && typeof col.render !== 'function')
        .map(col => ({ key: col.key, label: String(col.label) })),
      filename: exportFilename,
      title: 'Table Export',
    });
  };

  const resolveRowActions = (row) => {
    const rowActions = typeof actions === 'function'
      ? actions(row)
      : Array.isArray(actions) && typeof actions[0] === 'function'
        ? actions[0](row)
        : actions;

    if (!Array.isArray(rowActions)) return [];

    return rowActions.filter((action) => {
      const isHidden = typeof action.hide === 'function' ? action.hide(row) : action.hide;
      return !isHidden;
    });
  };

  const handleRowContextMenu = (event, row, rowKey) => {
    const rowActions = resolveRowActions(row);
    if (!rowActions.length) return;

    event.preventDefault();
    event.stopPropagation();

    const estimatedMenuWidth = 280;
    const estimatedMenuHeight = Math.max(64, rowActions.length * 44 + 52);
    const viewportPadding = 12;

    let x = event.clientX;
    let y = event.clientY;

    if (x + estimatedMenuWidth > window.innerWidth - viewportPadding) {
      x = window.innerWidth - estimatedMenuWidth - viewportPadding;
    }
    if (y + estimatedMenuHeight > window.innerHeight - viewportPadding) {
      y = window.innerHeight - estimatedMenuHeight - viewportPadding;
    }

    setActiveRowKey(rowKey);
    setContextMenu({
      isOpen: true,
      x: Math.max(viewportPadding, x),
      y: Math.max(viewportPadding, y),
      row,
      rowActions,
    });
  };

  const handleContextMenuActionClick = (action) => {
    if (typeof action.onClick === 'function' && contextMenu.row) {
      action.onClick(contextMenu.row);
    }
    setContextMenu((prev) => ({ ...prev, isOpen: false }));
  };

  const renderMobileActions = (row) => {
    const rowActions = resolveRowActions(row);
    if (!rowActions.length) return null;

    return (
      <div className={styles.mobileActionsRow}>
        {rowActions.map((action, index) => {
          const isDisabled = typeof action.disabled === 'function'
            ? action.disabled(row)
            : action.disabled;
          const itemLabel = action.label || action.tooltip || `${t('actionText')} ${index + 1}`;

          return (
            <button
              key={`${itemLabel}-${index}`}
              type="button"
              className={`${styles.mobileActionButton} ${action.className === 'delete' ? styles.mobileActionButtonDanger : ''} ${action.className === 'warning' ? styles.mobileActionButtonWarning : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!isDisabled && typeof action.onClick === 'function') {
                  action.onClick(row);
                }
              }}
              disabled={isDisabled}
              title={itemLabel}
            >
              <span className={styles.mobileActionIcon}>{action.icon || <FontAwesomeIcon icon={faFileAlt} />}</span>
              <span className={styles.mobileActionLabel}>{itemLabel}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const handleAISearch = async () => {
    if (!AISearchEndpoint) return;
    setAiLoading(true);
    try {
      const url = `${AISearchEndpoint}?PageSize=${pageSize}&PageNumber=${pageNumber}&Word=${searchTerm}`;
      const result = await (await import('../../Services/ApiServices')).default.get(url);
      if (onSearch) {
        onSearch(searchTerm, result);
      }
    } catch (err) {
    } finally {
      setAiLoading(false);
    }
  };

  const filteredAndSortedData = useMemo(() => {
    let filteredData = data;
    if (sortConfig) {
      filteredData = [...filteredData].sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        if (aValue === bValue) return 0;
        const comparison = aValue < bValue ? -1 : 1;
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }
    return filteredData;
  }, [data, sortConfig]);

  const groupedData = useMemo(() => {
    if (!useGroupBy || !groupByColumn) return null;

    const groups = {};
    const colDef = columns.find(c => c.key === groupByColumn);

    filteredAndSortedData.forEach((row, index) => {
      let rawValue = row[groupByColumn];
      if (rawValue === null || rawValue === undefined) rawValue = '';

      const groupKey = String(rawValue);
      const groupLabel = `${colDef?.label || ''}: ${groupKey}`;

      if (!groups[groupKey]) {
        groups[groupKey] = {
          name: groupLabel,
          key: groupKey,
          rows: []
        };
      }
      groups[groupKey].rows.push({ ...row, _originalIndex: index });
    });

    return Object.values(groups);
  }, [filteredAndSortedData, useGroupBy, groupByColumn, columns]);

  const toggleGroupCollapse = (groupKey) => {
    const newCollapsed = new Set(collapsedGroups);
    if (newCollapsed.has(groupKey)) {
      newCollapsed.delete(groupKey);
    } else {
      newCollapsed.add(groupKey);
    }
    setCollapsedGroups(newCollapsed);
  };

  const isDataEmpty = !filteredAndSortedData || filteredAndSortedData.length === 0;

  const renderCell = (column, value, row) => {
    if (column.link && row) {
      const to = column.link(row);
      return (
        <div className={`${styles.cellContent} ${styles[`align${column.align || 'center'}`]}`}>
          <Link to={to} className={styles.linkCell}>{value}</Link>
        </div>
      );
    }
    const cellContent = column.render
      ? column.render(value, row)
      : column.key === 'status'
        ? (
          <span className={`${styles.statusBadge} ${value.toLowerCase() === setStatusWordColor.toLowerCase()
            ? styles.statusActive
            : styles.statusInactive
            }`}>
            {value}
          </span>
        )
        : value;
    return (
      <div className={`${styles.cellContent} ${styles[`align${column.align || 'center'}`]}`}>
        {cellContent}
      </div>
    );
  };

  const getMobileCardTitle = (row) => {
    const primaryColumn = allColumns.find((col) => col.key !== 'selection');
    if (!primaryColumn) {
      return t('recordText');
    }
    const value = row[primaryColumn.key];
    if (value === null || value === undefined || value === '') {
      return primaryColumn.label;
    }
    return value;
  };

  const handleResizeStart = (e, columnKey) => {
    e.preventDefault();
    setIsResizing(true);
    setResizeStartX(e.clientX);
    setResizeColumnKey(columnKey);
    setResizeStartWidth(columnWidths[columnKey] || 150);
  };

  const handleDragStart = (e, columnKey) => {
    if (!isColumnDraggable) return;
    setDraggedColumnKey(columnKey);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', columnKey);
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragOver = (e, columnKey) => {
    if (!isColumnDraggable || !draggedColumnKey || draggedColumnKey === columnKey) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumnKey(columnKey);
  };

  const handleDragLeave = () => {
    setDragOverColumnKey(null);
  };

  const handleDrop = (e, targetColumnKey) => {
    if (!isColumnDraggable || !draggedColumnKey || draggedColumnKey === targetColumnKey) return;
    e.preventDefault();

    const currentOrder = [...columnOrder];
    const draggedIndex = currentOrder.indexOf(draggedColumnKey);
    const targetIndex = currentOrder.indexOf(targetColumnKey);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Remove dragged column from its current position
    currentOrder.splice(draggedIndex, 1);
    // Insert at new position
    currentOrder.splice(targetIndex, 0, draggedColumnKey);

    setColumnOrder(currentOrder);
    setDraggedColumnKey(null);
    setDragOverColumnKey(null);
  };

  const handleDragEnd = (e) => {
    if (!isColumnDraggable) return;
    e.currentTarget.style.opacity = '1';
    setDraggedColumnKey(null);
    setDragOverColumnKey(null);
  };

  const toggleColumnVisibility = (columnKey) => {
    const newVisibleKeys = new Set(visibleColumnKeys);
    if (newVisibleKeys.has(columnKey)) {
      if (newVisibleKeys.size > 1) {
        newVisibleKeys.delete(columnKey);
      }
    } else {
      newVisibleKeys.add(columnKey);
    }
    setVisibleColumnKeys(newVisibleKeys);
  };

  const resetColumns = () => {
    setVisibleColumnKeys(new Set(columns.map(col => col.key)));
  };

  const handleSelectAllColumnsToggle = () => {
    if (visibleColumnKeys.size === columns.length) {
      // If all selected, keep at least one (first one) or clear all? 
      // User expected select all checkbox, so if checked -> all, if unchecked -> none (but keep at least one to avoid empty table)
      // Actually usually it's just all or none. Let's do all or none, but maybe keep first column if none.
      // Better to just follow standard: all or none.
      setVisibleColumnKeys(new Set([columns[0].key]));
    } else {
      setVisibleColumnKeys(new Set(columns.map(col => col.key)));
    }
  };

  const allColumns = useMemo(() => {
    const tableColumns = [];
    const selectionColumn = {
      key: 'selection',
      label: (
        <input
          type="checkbox"
          checked={selectedRows.size === filteredAndSortedData.length && filteredAndSortedData.length > 0}
          onChange={handleSelectAll}
          className={styles.checkbox}
          aria-label="Select all rows"
        />
      ),
      align: 'center',
      width: 50,
      minWidth: 50,
      fixedWidth: 50,
    };

    // Build column map for easy lookup
    const columnMap = new Map();
    if (multiSelect) {
      columnMap.set('selection', selectionColumn);
    }
    columns.forEach((col) => columnMap.set(col.key, col));

    let filteredColumns = columns;
    if (isColumnSelectable && visibleColumnKeys.size > 0) {
      filteredColumns = columns.filter(col => visibleColumnKeys.has(col.key));
    }

    // If column dragging is enabled and we have an order, use it
    if (isColumnDraggable && columnOrder.length > 0) {
      const orderedColumns = [];
      columnOrder.forEach((key) => {
        // Only add if it's either a fixed column (selection) or in the visible columns list
        if (key === 'selection' || !isColumnSelectable || visibleColumnKeys.has(key)) {
          const col = columnMap.get(key);
          if (col) {
            orderedColumns.push(col);
          }
        }
      });
      // Add any columns that might not be in the order (new columns)
      columnMap.forEach((col, key) => {
        if (!columnOrder.includes(key)) {
          if (key === 'selection' || !isColumnSelectable || visibleColumnKeys.has(key)) {
            orderedColumns.push(col);
          }
        }
      });
      return orderedColumns;
    }

    // Default order
    if (multiSelect) {
      tableColumns.push(selectionColumn);
    }
    tableColumns.push(...filteredColumns);
    return tableColumns;
  }, [columns, selectedRows, filteredAndSortedData, multiSelect, isColumnDraggable, columnOrder, isColumnSelectable, visibleColumnKeys]);

  // Calculate column widths to fit table
  const calculatedColumnWidths = useMemo(() => {
    if (allColumns.length === 0) return {};

    const fixedWidthColumns = allColumns.filter(col => col.fixedWidth);
    const fixedWidthTotal = fixedWidthColumns.reduce((sum, col) => sum + (col.fixedWidth || 0), 0);
    const flexibleColumns = allColumns.filter(col => !col.fixedWidth);
    const flexibleCount = flexibleColumns.length;

    // Calculate width for flexible columns (distribute remaining space equally)
    const flexibleWidth = flexibleCount > 0 ? `calc((100% - ${fixedWidthTotal}px) / ${flexibleCount})` : 'auto';

    const widths = {};
    allColumns.forEach(col => {
      if (col.fixedWidth) {
        widths[col.key] = `${col.fixedWidth}px`;
      } else {
        widths[col.key] = flexibleWidth;
      }
    });

    return widths;
  }, [allColumns]);

  const getColumnWidth = (columnKey) => {
    if (columnResizable) {
      return columnWidths[columnKey] || (columnKey === 'selection' ? 50 : 150);
    }

    return calculatedColumnWidths[columnKey] || 'auto';
  };

  const renderDesktopLoadingRows = () => {
    const loadingRowCount = Math.max(3, Math.min(pageSize || 5, 8));
    return Array.from({ length: loadingRowCount }).map((_, rowIndex) => (
      <tr key={`loading-row-${rowIndex}`} className={styles.tableRow}>
        {allColumns.map((column) => (
          <td key={`${column.key}-${rowIndex}`} className={styles.tableCell} style={{ boxSizing: 'border-box' }}>
            <div className={styles.skeletonLine} />
          </td>
        ))}
      </tr>
    ));
  };

  const dataColumnsCount = allColumns.filter((column) => column.key !== 'selection').length;

  return (
    <div className={styles.tableContainer}>
      <div className={styles.searchContainer}>
        <div className={styles.searchSection}>
          {useAISearch && (
            <button
              onClick={handleAISearch}
              className={`${styles.softTrendAIButton} ${aiLoading ? styles.loading : ''}`}
              style={{ marginRight: 12, marginBottom: 8 }}
              disabled={aiLoading}
              title="Search with SoftTrendAI"
              type="button"
            >
              <span className="sparkle" aria-hidden="true">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g filter="url(#glow)">
                    <path d="M16 4 L18 14 L28 16 L18 18 L16 28 L14 18 L4 16 L14 14 Z" fill="#fff" fillOpacity="0.7" />
                  </g>
                  <defs>
                    <filter id="glow" x="0" y="0" width="32" height="32" filterUnits="userSpaceOnUse">
                      <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
                      <feMerge>
                        <feMergeNode in="coloredBlur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>
                </svg>
              </span>
              <span className="aiIcon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="11" cy="11" r="10" fill="#fff" fillOpacity="0.13" />
                  <path d="M11 5.5V16.5M5.5 11H16.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </span>
              <span className="aiText">
                {aiLoading ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ marginRight: 6 }}>Searching</span>
                    <span style={{ width: 18, height: 18, display: 'inline-block' }}>
                      <svg viewBox="0 0 50 50" style={{ width: 18, height: 18 }}>
                        <circle cx="25" cy="25" r="20" fill="none" stroke="#fff" strokeWidth="5" strokeDasharray="31.4 31.4" strokeLinecap="round">
                          <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.8s" repeatCount="indefinite" />
                        </circle>
                      </svg>
                    </span>
                  </span>
                ) : (
                  <>
                    <span style={{ fontWeight: 700, letterSpacing: 1 }}>SoftTrend</span>
                    <span style={{ fontWeight: 400, marginLeft: 2 }}>AI</span>
                  </>
                )}
              </span>
            </button>
          )}
          <SearchInput
            value={searchTerm}
            onChange={handleSearch}
            onClear={handleClearSearch}
            onSearch={handleSearchClick}
            placeholder={searchPlaceholder || t('searchInAllColumns')}
            inputRef={searchInputRef}
          />
          {useGroupBy && (
            <div className={styles.groupByWrapper}>
              <Dropdown
                trigger={
                  <button type="button" className={styles.customDropdownTrigger}>
                    {groupByColumn ? columns.find(c => c.key === groupByColumn)?.label : t('groupBy')}
                    <FontAwesomeIcon icon={faChevronDown} style={{ marginInlineStart: '8px' }} />
                  </button>
                }
              >
                <DropdownItem onClick={() => setGroupByColumn(null)}>
                  <em>{t('noGrouping')}</em>
                </DropdownItem>
                <DropdownDivider />
                {columns.filter(c => c.key !== 'selection' && c.key !== 'actions').map(col => (
                  <DropdownItem 
                    key={col.key} 
                    onClick={() => setGroupByColumn(col.key)}
                    icon={groupByColumn === col.key ? <FontAwesomeIcon icon={faCheck} /> : <span style={{ width: 16 }} />}
                  >
                    {col.label}
                  </DropdownItem>
                ))}
              </Dropdown>
            </div>
          )}

          {isColumnSelectable && (
            <div className={styles.columnSelectorWrapper}>
              <Dropdown
                trigger={
                  <button type="button" className={styles.customDropdownTrigger}>
                    <FontAwesomeIcon icon={faColumns} style={{ marginInlineEnd: 8 }} />
                    {visibleColumnKeys.size === columns.length ? t('selectAll') : `${visibleColumnKeys.size} ${t('columnsText')}`}
                    <FontAwesomeIcon icon={faChevronDown} style={{ marginInlineStart: '8px' }} />
                  </button>
                }
              >
                <DropdownCheckboxItem
                  checked={visibleColumnKeys.size === columns.length}
                  onChange={handleSelectAllColumnsToggle}
                >
                  <strong>{t('selectAll')}</strong>
                </DropdownCheckboxItem>
                <DropdownDivider />
                {columns.map((col) => (
                  <DropdownCheckboxItem
                    key={col.key}
                    checked={visibleColumnKeys.has(col.key)}
                    onChange={() => toggleColumnVisibility(col.key)}
                  >
                    {col.label}
                  </DropdownCheckboxItem>
                ))}
              </Dropdown>
            </div>
          )}

          {isPaginated && (
            <div className={styles.pageSizeWrapper} title="عدد السجلات في الصفحة">
              <Dropdown
                trigger={
                  <button type="button" className={styles.customDropdownTrigger}>
                    {t('pageSizeLabel')} {pageSize}
                    <FontAwesomeIcon icon={faChevronDown} style={{ marginInlineStart: '8px' }} />
                  </button>
                }
              >
                {[5, 10, 20, 50, 100, 500].map((size) => (
                  <DropdownItem 
                    key={size} 
                    onClick={() => onPageSizeChange?.(size)}
                    icon={pageSize === size ? <FontAwesomeIcon icon={faCheck} /> : <span style={{ width: 16 }} />}
                  >
                    {size}
                  </DropdownItem>
                ))}
              </Dropdown>
            </div>
          )}
          {actions && actions.length > 0 && !isMobileView && (
            <div className={styles.contextHint} aria-label={t('rightClickForActions')}>
              <span className={styles.contextHintIcon} role="img" aria-label="mouse">🖱️</span>
              <span className={styles.contextHintTooltip}>{t('rightClickForActions')}</span>
            </div>
          )}
        </div>

        <div className={styles.summaryBar}>
          <SummaryPill
            label={t('recordCountLabel')}
            value={isPaginated ? totalRecords : filteredAndSortedData.length}
            tone="primary"
          />
          <SummaryPill
            label={t('currentlyDisplayedLabel')}
            value={filteredAndSortedData.length}
            tone="info"
          />
          <SummaryPill
            label={t('activeColumnsLabel')}
            value={dataColumnsCount}
            tone="default"
          />
          {multiSelect && (
            <SummaryPill
              label={t('selectedLabel')}
              value={selectedRows.size}
              tone={selectedRows.size > 0 ? 'success' : 'default'}
            />
          )}

          <div className={styles.exportSection} style={{ marginInlineStart: 'auto' }}>
            {multiSelect && onDeleteSelected && (
              <button
                onClick={handleDeleteSelected}
                className={`${styles.deleteSelectedButton} ${showDeleteButton ? styles.visible : ''}`}
                title={`Delete ${selectedRows.size} selected items`}
                style={{
                  display: 'inline-flex',
                  opacity: showDeleteButton ? 1 : 0,
                  visibility: showDeleteButton ? 'visible' : 'hidden',
                  transform: showDeleteButton ? 'translateY(0)' : 'translateY(20px)',
                  pointerEvents: showDeleteButton ? 'auto' : 'none'
                }}
              >
                <FontAwesomeIcon icon={faTrash} className={styles.deleteIcon} />
                <span>Delete Selected ({selectedRows.size})</span>
              </button>
            )}
            {multiSelect && multiSelectAPI && (
              <button
                onClick={handleMultiSelectDelete}
                className={`${styles.deleteSelectedButton} ${showDeleteButton ? styles.visible : ''}`}
                disabled={multiDeleteLoading || selectedRows.size === 0}
                title={t('softGrid_deleteSelectedBtn')}
                style={{
                  display: 'inline-flex',
                  opacity: showDeleteButton ? 1 : 0,
                  visibility: showDeleteButton ? 'visible' : 'hidden',
                  transform: showDeleteButton ? 'translateY(0)' : 'translateY(20px)',
                  pointerEvents: showDeleteButton ? 'auto' : 'none'
                }}
              >
                <FontAwesomeIcon icon={faTrash} className={styles.deleteIcon} />
                <span>
                  {multiDeleteLoading
                    ? t('deleting')
                    : `${t('softGrid_deleteSelectedBtn')} (${selectedRows.size})`
                  }
                </span>
              </button>
            )}
            {useExportButtons && (
              <div className={styles.exportButtons}>
                <button
                  onClick={handleExportExcel}
                  className={styles.exportButton}
                  title="Export to Excel"
                  disabled={isLoading || !filteredAndSortedData || filteredAndSortedData.length === 0}
                  style={{
                    opacity: (isLoading || !filteredAndSortedData || filteredAndSortedData.length === 0) ? 0.5 : 1,
                    cursor: (isLoading || !filteredAndSortedData || filteredAndSortedData.length === 0) ? 'not-allowed' : 'pointer'
                  }}
                >
                  <FontAwesomeIcon icon={faDownload} className={styles.exportIcon} />
                  <span>Excel</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        ref={tableWrapperRef}
        className={`${styles.tableWrapper} ${hasScrollTop ? styles.tableWrapperScrolled : ''} ${showScrollShadowLeft ? styles.scrollShadowLeft : ''} ${showScrollShadowRight ? styles.scrollShadowRight : ''}`}
      >
        {isMobileView ? (
          <div className={styles.mobileCardsWrapper}>
            {isLoading ? (
              <div className={styles.mobileSkeletonContainer}>
                {Array.from({ length: 3 }).map((_, idx) => (
                  <div key={`mobile-skeleton-${idx}`} className={styles.mobileSkeletonCard}>
                    <div className={styles.skeletonLine} />
                    <div className={styles.skeletonLine} />
                    <div className={`${styles.skeletonLine} ${styles.skeletonLineShort}`} />
                  </div>
                ))}
              </div>
            ) : isDataEmpty ? (
              <div className={styles.emptyState}>
                <EmptyState
                  message={emptyStateMessage || t('noDataAvailable')}
                  isSearch={!!searchTerm}
                  onAction={handleClearSearch}
                />
              </div>
            ) : groupedData ? (
              groupedData.map((group) => (
                <React.Fragment key={`mobile-group-${group.key}`}>
                  <div
                    className={styles.mobileGroupHeader}
                    onClick={() => toggleGroupCollapse(group.key)}
                  >
                    <div className={styles.mobileGroupHeaderLeft}>
                      <span className={styles.groupHeaderName}>{group.name}</span>
                      <span className={styles.groupCountBadge}>
                        {t('groupItemsCount').replace('{count}', group.rows.length)}
                      </span>
                    </div>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className={`${styles.groupChevron} ${collapsedGroups.has(group.key) ? '' : styles.groupChevronExpanded}`}
                    />
                  </div>
                  {!collapsedGroups.has(group.key) && group.rows.map((row) => {
                    const rowIndex = row._originalIndex;
                    const rowKey = row.id || rowIndex;
                    return (
                      <div
                        key={rowKey}
                        className={`${styles.mobileCard} ${selectedRows.has(rowIndex) ? styles.selectedRow : ''} ${activeRowKey === rowKey ? styles.activeRow : ''}`}
                        onClick={() => setActiveRowKey(rowKey)}
                      >
                        <div className={styles.mobileCardHeader}>
                          <div className={styles.mobileCardTitleGroup}>
                            <h3 className={styles.mobileCardTitle}>{String(getMobileCardTitle(row))}</h3>
                          </div>
                          {multiSelect && (
                            <input
                              type="checkbox"
                              checked={selectedRows.has(rowIndex)}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleSelectRow(rowIndex);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className={styles.checkbox}
                              aria-label={`Select row ${rowIndex + 1}`}
                            />
                          )}
                        </div>

                        <div className={styles.mobileCardBody}>
                          {allColumns
                            .filter((column) => column.key !== 'selection')
                            .map((column) => (
                              <div key={`${rowIndex}-${column.key}`} className={styles.mobileInfoRow}>
                                <span className={styles.mobileInfoLabel}>{column.label}</span>
                                <div className={styles.mobileInfoValue}>
                                  {renderCell(column, row[column.key], row)}
                                </div>
                              </div>
                            ))}
                        </div>

                        {actions.length > 0 && (
                          <div className={styles.mobileCardFooter}>
                            {renderMobileActions(row)}
                          </div>
                        )}

                      </div>
                    );
                  })}
                </React.Fragment>
              ))
            ) : (
              filteredAndSortedData.map((row, rowIndex) => (
                (() => {
                  const rowKey = row.id || rowIndex;
                  return (
                    <div
                      key={rowKey}
                      className={`${styles.mobileCard} ${selectedRows.has(rowIndex) ? styles.selectedRow : ''} ${activeRowKey === rowKey ? styles.activeRow : ''}`}
                      onClick={() => setActiveRowKey(rowKey)}
                    >
                      <div className={styles.mobileCardHeader}>
                        <div className={styles.mobileCardTitleGroup}>
                          <h3 className={styles.mobileCardTitle}>{String(getMobileCardTitle(row))}</h3>
                        </div>
                        {multiSelect && (
                          <input
                            type="checkbox"
                            checked={selectedRows.has(rowIndex)}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleSelectRow(rowIndex);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className={styles.checkbox}
                            aria-label={`Select row ${rowIndex + 1}`}
                          />
                        )}
                      </div>

                      <div className={styles.mobileCardBody}>
                        {allColumns
                          .filter((column) => column.key !== 'selection')
                          .map((column) => (
                            <div key={`${rowIndex}-${column.key}`} className={styles.mobileInfoRow}>
                              <span className={styles.mobileInfoLabel}>{column.label}</span>
                              <div className={styles.mobileInfoValue}>
                                {renderCell(column, row[column.key], row)}
                              </div>
                            </div>
                          ))}
                      </div>

                      {actions.length > 0 && (
                        <div className={styles.mobileCardFooter}>
                          {renderMobileActions(row)}
                        </div>
                      )}

                    </div>
                  );
                })()
              ))
            )}
          </div>
        ) : (
          <table ref={tableRef} className={styles.table} style={{ tableLayout: 'fixed', width: '100%', direction }}>
            <colgroup>
              {allColumns.map((column) => (
                <col
                  key={column.key}
                  style={{ width: getColumnWidth(column.key) }}
                />
              ))}
            </colgroup>
            <thead>
              <tr>
                {allColumns.map((column) => (
                  <th
                    key={column.key}
                    className={`${styles.tableHeader} ${column.sortable ? styles.sortable : ''
                      } ${styles[`align${column.align || 'center'}`]} ${columnResizable ? styles.resizable : ''
                      } ${isColumnDraggable ? styles.draggable : ''} ${draggedColumnKey === column.key ? styles.dragging : ''
                      } ${dragOverColumnKey === column.key ? styles.dragOver : ''}
                      }`}
                    onClick={() => {
                      if (column.sortable && !draggedColumnKey) {
                        handleSort(column.key);
                      }
                    }}
                    draggable={isColumnDraggable && column.key !== 'selection'}
                    onDragStart={(e) => handleDragStart(e, column.key)}
                    onDragOver={(e) => handleDragOver(e, column.key)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, column.key)}
                    onDragEnd={handleDragEnd}
                  >
                    <div className={styles.headerContent}>
                      {isColumnDraggable && column.key !== 'selection' && (
                        <span className={styles.dragHandle} title="Drag to reorder column">
                          ⋮⋮
                        </span>
                      )}
                      {column.label}
                      {column.sortable && (
                        <span className={styles.sortIndicator}>
                          <FontAwesomeIcon
                            icon={faChevronUp}
                            className={`${styles.sortChevron} ${sortConfig?.key === column.key && sortConfig.direction === 'asc' ? styles.sortChevronActive : ''}`}
                          />
                          <FontAwesomeIcon
                            icon={faChevronDown}
                            className={`${styles.sortChevron} ${sortConfig?.key === column.key && sortConfig.direction === 'desc' ? styles.sortChevronActive : ''}`}
                          />
                        </span>
                      )}
                    </div>
                    {columnResizable && (
                      <div
                        className={styles.resizeHandle}
                        onMouseDown={(e) => handleResizeStart(e, column.key)}
                        title="Drag to resize column"
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                renderDesktopLoadingRows()
              ) : isDataEmpty ? (
                <tr>
                  <td colSpan={allColumns.length} className={styles.emptyState}>
                    <EmptyState
                      message={emptyStateMessage || t('noDataAvailable')}
                      isSearch={!!searchTerm}
                      onAction={handleClearSearch}
                    />
                  </td>
                </tr>
              ) : groupedData ? (
                groupedData.map((group) => (
                  <React.Fragment key={`group-${group.key}`}>
                    <tr
                      className={styles.groupHeaderRow}
                      onClick={() => toggleGroupCollapse(group.key)}
                    >
                      <td colSpan={allColumns.length} style={{ padding: 0 }}>
                        <div className={styles.groupHeaderContent}>
                          <FontAwesomeIcon
                            icon={faChevronDown}
                            className={`${styles.groupChevron} ${collapsedGroups.has(group.key) ? '' : styles.groupChevronExpanded}`}
                          />
                          <span className={styles.groupHeaderName}>{group.name}</span>
                          <span className={styles.groupCountBadge}>
                            {t('groupItemsCount').replace('{count}', group.rows.length)}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {!collapsedGroups.has(group.key) && group.rows.map((row) => {
                      const rowIndex = row._originalIndex;
                      const rowKey = row.id || rowIndex;
                      return (
                        <tr
                          key={rowKey}
                          dir={direction}
                          className={`${styles.tableRow} ${selectedRows.has(rowIndex) ? styles.selectedRow : ''} ${activeRowKey === rowKey ? styles.activeRow : ''}`}
                          onClick={() => setActiveRowKey(rowKey)}
                          onContextMenu={(event) => handleRowContextMenu(event, row, rowKey)}
                          title={actions.length > 0 ? t('rightClickForActions') : undefined}
                        >
                          {allColumns.map((column) => (
                            <td
                              key={column.key}
                              className={styles.tableCell}
                              style={{ boxSizing: 'border-box' }}
                            >
                              {column.key === 'selection' ? (
                                <input
                                  type="checkbox"
                                  checked={selectedRows.has(rowIndex)}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleSelectRow(rowIndex);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  className={styles.checkbox}
                                  aria-label={`Select row ${rowIndex + 1}`}
                                />
                              ) : (
                                renderCell(column, row[column.key], row)
                              )}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))
              ) : (
                filteredAndSortedData.map((row, rowIndex) => (
                  (() => {
                    const rowKey = row.id || rowIndex;
                    return (
                      <tr
                        key={rowKey}
                        dir={direction}
                        className={`${styles.tableRow} ${selectedRows.has(rowIndex) ? styles.selectedRow : ''} ${activeRowKey === rowKey ? styles.activeRow : ''}`}
                        onClick={() => setActiveRowKey(rowKey)}
                        onContextMenu={(event) => handleRowContextMenu(event, row, rowKey)}
                        title={actions.length > 0 ? t('rightClickForActions') : undefined}
                      >
                        {allColumns.map((column) => (
                          <td
                            key={column.key}
                            className={styles.tableCell}
                            style={{ boxSizing: 'border-box' }}
                          >
                            {column.key === 'selection' ? (
                              <input
                                type="checkbox"
                                checked={selectedRows.has(rowIndex)}
                                onChange={() => handleSelectRow(rowIndex)}
                                className={styles.checkbox}
                                aria-label={`Select row ${rowIndex + 1}`}
                              />
                            ) : (
                              renderCell(column, row[column.key], row)
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })()
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {
        isPaginated && (
          <Pagination
            currentPage={pageNumber}
            totalPages={totalPages}
            totalRecords={totalRecords}
            onPageChange={onPageChange || (() => { })}
          />
        )
      }

      {contextMenu.isOpen && typeof document !== 'undefined' && createPortal(
        <div
          ref={contextMenuRef}
          className={styles.contextMenu}
          style={{ top: contextMenu.y, left: contextMenu.x }}
          role="menu"
          aria-label="Row actions"
        >
          <div className={styles.contextMenuTitle}>{t('recordActionsLabel')}</div>
          {contextMenu.rowActions.map((action, index) => {
            const isDisabled = typeof action.disabled === 'function'
              ? action.disabled(contextMenu.row)
              : action.disabled;
            const itemLabel = action.label || action.tooltip || `${t('actionText')} ${index + 1}`;

            return (
              <button
                key={`${itemLabel}-${index}`}
                type="button"
                role="menuitem"
                className={`${styles.contextMenuItem} ${action.className === 'delete' ? styles.contextMenuItemDanger : ''} ${action.className === 'warning' ? styles.contextMenuItemWarning : ''}`}
                onClick={() => handleContextMenuActionClick(action)}
                disabled={isDisabled}
                title={itemLabel}
              >
                <span className={styles.contextMenuItemIcon}>{action.icon || <FontAwesomeIcon icon={faFileAlt} />}</span>
                <span className={styles.contextMenuItemLabel}>{itemLabel}</span>
              </button>
            );
          })}
        </div>,
        document.body
      )}

      <DeleteAlert
        isOpen={showMultiDeleteAlert}
        onClose={() => setShowMultiDeleteAlert(false)}
        onConfirm={confirmMultiSelectDelete}
        title={t('softGrid_deleteSelectedBtn')}
        message={t('softGrid_confirmDeleteSelected').replace('{count}', String(selectedRows.size))}
        loading={multiDeleteLoading}
      />
    </div >
  );
};

export default SoftGrid; 